import { useState, useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, X, Smartphone } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { DevDeviceBadge } from "@/components/dev-device-badge";
import { DownloadAppButton } from "@/components/download-app-button";
import { EmptyState } from "@/components/empty-state";
import { TableSkeleton } from "@/components/loading-skeletons";
import { PageHeader } from "@/components/layout/page-header";
import { SearchInput } from "@/components/search-input";
import { SortButton } from "@/components/sort-button";
import { RowLink, TableLinkRow } from "@/components/table-link-row";
import { useDevicesQuery } from "@/hooks/use-devices";
import { PlatformIcon } from "@/lib/device-platform";

const AUTO_REFRESH_INTERVAL = 30000;

type SortField = "name" | "user_id" | "notifications_enabled";
type SortOrder = "asc" | "desc";

export function DevicesPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const queryClient = useQueryClient();

  const { data: devices, isLoading, error } = useDevicesQuery({
    refetchInterval: AUTO_REFRESH_INTERVAL,
  });

  const filteredAndSortedDevices = useMemo(() => {
    if (!devices) return [];

    let result = devices;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(
        (device) =>
          device.name.toLowerCase().includes(query) ||
          device.user_id.toLowerCase().includes(query)
      );
    }

    result = [...result].sort((a, b) => {
      let comparison: number;
      if (sortField === "name") {
        comparison = a.name.localeCompare(b.name);
      } else if (sortField === "user_id") {
        comparison = a.user_id.localeCompare(b.user_id);
      } else {
        comparison =
          (a.notifications_enabled ? 1 : 0) - (b.notifications_enabled ? 1 : 0);
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });

    return result;
  }, [devices, searchQuery, sortField, sortOrder]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const getSortDirection = (field: SortField) =>
    sortField === field ? sortOrder : null;

  const handleDeviceConnected = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["devices"] });
  }, [queryClient]);

  return (
    <div className="flex flex-1 flex-col gap-6">
      <PageHeader
        title="Devices"
        actions={<DownloadAppButton onDeviceConnected={handleDeviceConnected} />}
      />

      <div className="flex flex-1 flex-col">
        {devices && devices.length > 0 && (
          <div className="border-b pb-4">
            <SearchInput
              placeholder="Search devices..."
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>
        )}

        {isLoading && <TableSkeleton />}

        {error && (
          <p className="py-16 text-center text-sm text-destructive">
            Failed to load devices
          </p>
        )}

        {devices && (
          <>
            {filteredAndSortedDevices.length === 0 && (
              <EmptyState
                icon={Smartphone}
                title={searchQuery.trim() ? "No results" : "No devices found"}
                description={
                  searchQuery.trim()
                    ? "No devices match your search."
                    : "Install the app on your phone to connect the first one."
                }
              >
                {!searchQuery.trim() && (
                  <DownloadAppButton onDeviceConnected={handleDeviceConnected} />
                )}
              </EmptyState>
            )}

            {filteredAndSortedDevices.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <SortButton
                        label="Device Name"
                        direction={getSortDirection("name")}
                        onClick={() => handleSort("name")}
                      />
                    </TableHead>
                    <TableHead className="md:w-56">
                      <SortButton
                        label="User ID"
                        direction={getSortDirection("user_id")}
                        onClick={() => handleSort("user_id")}
                      />
                    </TableHead>
                    <TableHead className="md:w-44">
                      <SortButton
                        label="Status"
                        direction={getSortDirection("notifications_enabled")}
                        onClick={() => handleSort("notifications_enabled")}
                      />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedDevices.map((device) => (
                    <TableLinkRow key={device.id} to={`/devices/${device.id}`}>
                      <TableCell>
                        <RowLink
                          to={`/devices/${device.id}`}
                          className="inline-flex items-center gap-2.5"
                        >
                          <PlatformIcon
                            platform={device.platform}
                            className="size-4 shrink-0 text-muted-foreground"
                          />
                          <span>{device.name}</span>
                          <DevDeviceBadge deviceId={device.id} />
                        </RowLink>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-xs text-muted-foreground">
                          {device.user_id}
                        </span>
                      </TableCell>
                      <TableCell>
                        {device.notifications_enabled ? (
                          <Badge variant="success">
                            <Check aria-hidden="true" />
                            Enabled
                          </Badge>
                        ) : (
                          <Badge variant="error">
                            <X aria-hidden="true" />
                            Disabled
                          </Badge>
                        )}
                      </TableCell>
                    </TableLinkRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </div>
    </div>
  );
}
